/**
 * Seed → Manual map: click inside the contour to place wells (ghost preview under
 * the cursor), drag pins to move them, click a pin to select its table row.
 */
import { Box } from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  CircleMarker,
  MapContainer,
  Marker,
  Polygon,
  Polyline,
  useMapEvents,
  ZoomControl,
} from 'react-leaflet';

import { MapBaseLayers } from '@/components/Map/mapLayers';
import { FitDataBounds, InvalidateOnResize } from '@/components/Scenarios/VoronoiMbMap';
import type { MbPoint } from '@/types/materialBalance';
import { latLngToLocalKm, localKmToLatLng, type GeoOrigin } from '@/utils/localKmProjection';
import { pointInPoly } from '@/utils/materialBalanceAutoPlace';
import type { SeedCandidate, SeedWellKind } from '@/utils/materialBalanceSeedManual';

type LatLngTuple = [number, number];

export type SeedMapWell = {
  id: string;
  name: string;
  kind: SeedWellKind;
  x: number;
  y: number;
};

const KIND_COLOR: Record<SeedWellKind, string> = {
  prod: '#16a34a',
  inj: '#2563eb',
};

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// A new icon instance makes react-leaflet call setIcon, which rebuilds the marker's
// drag handler and aborts an in-progress drag — so icons must be stable per look.
const pinIconCache = new Map<string, L.DivIcon>();

function pinIcon(label: string, kind: SeedWellKind, selected: boolean) {
  const key = `${kind}|${selected ? 1 : 0}|${label}`;
  let icon = pinIconCache.get(key);
  if (!icon) {
    icon = buildPinIcon(label, kind, selected);
    pinIconCache.set(key, icon);
  }
  return icon;
}

function buildPinIcon(label: string, kind: SeedWellKind, selected: boolean) {
  const size = selected ? 18 : 14;
  return L.divIcon({
    className: 'seed-manual-pin',
    html: `<div style="position:relative;width:${size}px;height:${size}px;">
      <div style="
        width:${size}px;height:${size}px;border-radius:50%;box-sizing:border-box;
        background:${KIND_COLOR[kind]};
        border:2px solid ${selected ? '#f59e0b' : '#0f172a'};
        box-shadow:0 1px 3px rgba(0,0,0,0.35);
      "></div>
      <span style="
        position:absolute;left:${size + 3}px;top:-4px;
        padding:1px 5px;border-radius:3px;
        background:rgba(255,255,255,0.92);color:#0f172a;
        border:1px solid ${selected ? '#f59e0b' : KIND_COLOR[kind]};
        font:600 10px/1.25 system-ui,sans-serif;white-space:nowrap;
        pointer-events:none;
      ">${escapeHtml(label)}</span>
    </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function PlacementEvents({
  origin,
  onHover,
  onClick,
}: {
  origin: GeoOrigin;
  onHover: (pt: MbPoint | null) => void;
  onClick: (pt: MbPoint) => void;
}) {
  useMapEvents({
    mousemove: (e) => onHover(latLngToLocalKm(e.latlng.lat, e.latlng.lng, origin)),
    mouseout: () => onHover(null),
    click: (e) => onClick(latLngToLocalKm(e.latlng.lat, e.latlng.lng, origin)),
  });
  return null;
}

export interface SeedManualMapProps {
  contour: MbPoint[];
  origin?: GeoOrigin | null;
  wells: SeedMapWell[];
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  /** Wells that a click at `pt` would add (already carved); null when the tool is not ready */
  placementAt?: ((pt: MbPoint) => SeedCandidate[]) | null;
  onPlace?: (candidates: SeedCandidate[]) => void;
  onMove?: (id: string, pt: MbPoint) => void;
  disabled?: boolean;
  height?: number | string;
}

export const SeedManualMap: React.FC<SeedManualMapProps> = ({
  contour,
  origin = null,
  wells,
  selectedId = null,
  onSelect,
  placementAt = null,
  onPlace,
  onMove,
  disabled = false,
  height = '100%',
}) => {
  const geoOrigin = useMemo<GeoOrigin>(
    () =>
      origin && Number.isFinite(origin.lat) && Number.isFinite(origin.lng)
        ? origin
        : { lat: 60, lng: 5 },
    [origin]
  );
  const toLL = useCallback(
    (p: { x: number; y: number }): LatLngTuple => {
      const g = localKmToLatLng(p.x, p.y, geoOrigin);
      return [g.lat, g.lng];
    },
    [geoOrigin]
  );

  const [hover, setHover] = useState<MbPoint | null>(null);
  const [dragging, setDragging] = useState(false);

  const contourLL = useMemo(() => contour.map(toLL), [contour, toLL]);
  // Fit once per contour — adding / moving wells must not move the camera
  const fitKey = `${geoOrigin.lat},${geoOrigin.lng}|${contour
    .map((p) => `${p.x},${p.y}`)
    .join(';')}`;
  const fitPositions = useMemo(
    () => (contourLL.length ? contourLL : wells.map(toLL)),
    [contourLL, wells, toLL]
  );

  const placing = Boolean(placementAt) && !disabled;
  const ghost = placing && hover && !dragging ? placementAt!(hover) : [];
  const ghostCenter = ghost.find((c) => c.center);

  const handleClick = (pt: MbPoint) => {
    if (!placing || !onPlace) return;
    const candidates = placementAt!(pt);
    if (candidates.length) onPlace(candidates);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height,
        minHeight: 240,
        borderRadius: 1,
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        position: 'relative',
        boxSizing: 'border-box',
        '& .leaflet-container': {
          height: '100%',
          width: '100%',
          zIndex: 0,
          background: '#e8eef5',
          cursor: placing ? 'crosshair !important' : undefined,
        },
        '& .seed-manual-pin': {
          background: 'transparent',
          border: 'none',
          cursor: disabled ? 'default' : 'move',
        },
      }}
    >
      <MapContainer
        center={contourLL[0] || [geoOrigin.lat, geoOrigin.lng]}
        zoom={14}
        minZoom={2}
        maxZoom={22}
        scrollWheelZoom
        zoomControl={false}
        doubleClickZoom={false}
        style={{ height: '100%', width: '100%' }}
      >
        <MapBaseLayers maxZoom={22} />
        <ZoomControl position='bottomright' />
        <InvalidateOnResize />
        <FitDataBounds positions={fitPositions} fitKey={fitKey} />
        <PlacementEvents origin={geoOrigin} onHover={setHover} onClick={handleClick} />

        {contourLL.length >= 2 && (
          <Polygon
            positions={contourLL}
            interactive={false}
            pathOptions={{ fillOpacity: 0.04, color: '#0f172a', weight: 2.5, opacity: 0.9 }}
          />
        )}

        {ghostCenter &&
          ghost
            .filter((c) => !c.center)
            .map((c, i) => (
              <Polyline
                key={`ghost-link-${i}`}
                positions={[toLL(ghostCenter), toLL(c)]}
                interactive={false}
                pathOptions={{ color: '#64748b', weight: 1.5, dashArray: '4 4', opacity: 0.8 }}
              />
            ))}
        {ghost.map((c, i) => (
          <CircleMarker
            key={`ghost-${i}`}
            center={toLL(c)}
            radius={7}
            interactive={false}
            pathOptions={{
              color: KIND_COLOR[c.kind],
              weight: 2,
              dashArray: '3 3',
              fillColor: KIND_COLOR[c.kind],
              fillOpacity: 0.3,
            }}
          />
        ))}

        {wells.map((w) => (
          <SeedPin
            key={w.id}
            well={w}
            selected={selectedId === w.id}
            disabled={disabled}
            toLL={toLL}
            onDragStart={() => setDragging(true)}
            onDragEnd={(ll, revert) => {
              setDragging(false);
              onSelect?.(w.id);
              const km = latLngToLocalKm(ll.lat, ll.lng, geoOrigin);
              if (contour.length >= 3 && !pointInPoly(km.x, km.y, contour)) {
                revert();
                return;
              }
              onMove?.(w.id, km);
            }}
            onClick={() => onSelect?.(w.id)}
          />
        ))}
      </MapContainer>
    </Box>
  );
};

function SeedPin({
  well,
  selected,
  disabled,
  toLL,
  onDragStart,
  onDragEnd,
  onClick,
}: {
  well: SeedMapWell;
  selected: boolean;
  disabled: boolean;
  toLL: (p: { x: number; y: number }) => LatLngTuple;
  onDragStart: () => void;
  onDragEnd: (ll: L.LatLng, revert: () => void) => void;
  onClick: () => void;
}) {
  // react-leaflet calls setLatLng whenever `position` changes identity, which
  // would snap a pin back mid-drag on any unrelated re-render (e.g. hover).
  const { x, y } = well;
  const position = useMemo(() => toLL({ x, y }), [toLL, x, y]);
  const handlers = useRef({ onDragStart, onDragEnd, onClick });
  handlers.current = { onDragStart, onDragEnd, onClick };
  const eventHandlers = useMemo<L.LeafletEventHandlerFnMap>(
    () => ({
      click: () => handlers.current.onClick(),
      dragstart: () => handlers.current.onDragStart(),
      dragend: (e) => {
        const marker = e.target as L.Marker;
        handlers.current.onDragEnd(marker.getLatLng(), () => marker.setLatLng(position));
      },
    }),
    [position]
  );
  return (
    <Marker
      position={position}
      draggable={!disabled}
      icon={pinIcon(well.name || '—', well.kind, selected)}
      zIndexOffset={selected ? 1000 : 0}
      eventHandlers={eventHandlers}
    />
  );
}
